import React, { memo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface VoiceCommandPanelProps {
  isListening: boolean;
  transcript: string;
  voiceTranscripts: string[];
  voiceStatus: string;
  toggleListening: () => void;
  permissionDenied: boolean;
  commandMapping: Record<
    string,
    { phrases: string[]; action: () => void; description: string }
  >;
}

// Use React.memo to prevent unnecessary re-renders
const VoiceCommandPanel = memo(function VoiceCommandPanel({
  isListening,
  transcript,
  voiceTranscripts,
  voiceStatus,
  toggleListening,
  permissionDenied,
  commandMapping,
}: VoiceCommandPanelProps) {
  // Memoize the command list to prevent unnecessary re-renders
  const commandList = React.useMemo(() => {
    return Object.entries(commandMapping).map(([key, command]) => (
      <div key={key} className="text-xs">
        <span className="font-medium">{command.description}:</span>{" "}
        <span className="text-muted-foreground">
          &quot;{command.phrases.join('", "')}&quot;
        </span>
      </div>
    ));
  }, [commandMapping]);

  // Memoize the transcript list to prevent unnecessary re-renders
  const transcriptList = React.useMemo(() => {
    if (voiceTranscripts.length === 0) {
      return (
        <div className="text-muted-foreground italic">
          No recent transcripts
        </div>
      );
    }

    return (
      <div className="space-y-1 text-muted-foreground">
        {voiceTranscripts.slice(1).map((t, i) => (
          <div key={i} className="truncate">
            &quot;{t}&quot;
          </div>
        ))}
      </div>
    );
  }, [voiceTranscripts]);

  return (
    <div className="mt-2 rounded-md overflow-hidden border border-muted">
      {/* Voice command header with status and controls */}
      <div className="flex items-center justify-between bg-muted/50 p-2 text-xs">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 mr-1"
            onClick={toggleListening}
            title={isListening ? "Mute microphone" : "Unmute microphone"}
            disabled={permissionDenied}
          >
            {isListening ? (
              <Mic className="h-4 w-4 text-green-500 animate-pulse" />
            ) : (
              <MicOff className="h-4 w-4 text-gray-400" />
            )}
            <span className="sr-only">
              {isListening ? "Mute microphone" : "Microphone paused"}
            </span>
          </Button>
          <span className="font-medium">
            {permissionDenied
              ? "Microphone access denied"
              : isListening
              ? "Listening"
              : "Microphone paused"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant={
              isListening
                ? "default"
                : permissionDenied
                ? "destructive"
                : "secondary"
            }
            className="text-[10px]"
          >
            {voiceStatus}
          </Badge>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 px-2">
                <span className="text-[10px]">Commands</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-2">
              <div className="space-y-1">
                <h4 className="font-medium text-xs mb-2">
                  Available Voice Commands
                </h4>
                {commandList}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Transcript display (collapsible) */}
      {isListening && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-6 text-xs justify-between"
            >
              <span>Recent transcripts</span>
              <ChevronDown className="h-3 w-3" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-2 text-xs bg-background/50 max-h-24 overflow-y-auto">
              {transcript && (
                <div className="font-medium text-primary mb-1">
                  &quot;{transcript}&quot;
                </div>
              )}
              {transcriptList}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
});

export default VoiceCommandPanel;
